import React, { useEffect, useState, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import {
    ClientSideRowModelModule,
    CellStyleModule,
    CustomFilterModule,
    ModuleRegistry,
    themeQuartz,
    RowStyleModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    PaginationModule,
    ValidationModule,
    PaginationChangedEvent,
    RowModelType,
    ColumnAutoSizeModule,
    TooltipModule,
} from 'ag-grid-community'

// Import Required Modules

// Register Modules
ModuleRegistry.registerModules([ClientSideRowModelModule, NumberFilterModule, DateFilterModule, PaginationModule, ValidationModule, CellStyleModule, CustomFilterModule, TextFilterModule, RowStyleModule, ColumnAutoSizeModule, TooltipModule])

import 'ag-grid-community/styles/ag-theme-alpine.css'
import TablePagination from './Pagination'
import Loader from './loader'
import { NoRecordsFound } from './Table/utils'

interface CustomAgGridProps {
    rowData: any[] | null | undefined
    columnDefs?: any[]
    defaultColDef?: any
    gridOptions?: any
    className?: string
    style?: React.CSSProperties
    pagination?: boolean
    paginationPageSize?: number
    paginationPageSizeSelector?: number[] | false
    totalRecords?: number
    currentPage?: number
    gridId?: string
    rowHeight?: any
    headerHeight?: any
    theme?: 'dark' | 'light'
    extraThemeProps?: any
    alternativeRowColor?: any
    gridRef: any
    overlayLoadingTemplate?: string | null
    loadingOverlayComponent?: any
    loadingOverlayComponentParams?: any
    noRowsOverlayComponent?: any
    onPaginationChanged?: (event: PaginationChangedEvent) => void
    onGridReady?: () => void
    onPageChanged?: (page: number) => void
    customPagination?: boolean
    getRowHeight?: any
    dataTestId?: string
    onSortChanged?: (params: any) => void
    rowModelType?: RowModelType
}

const CustomAgGrid: React.FC<CustomAgGridProps> = ({
    rowData,
    columnDefs,
    defaultColDef = { sortable: true, filter: true },
    gridOptions = {},
    className = 'ag-theme w-full',
    style = { height: '100%', width: '100%' },
    pagination = true,
    paginationPageSize = 20,
    paginationPageSizeSelector = [20, 30, 50, 100],
    totalRecords = 1,
    currentPage = 1,
    gridId = 'myGrid',
    rowHeight = (globalThis as any).innerWidth < 1920 ? 30 : 45,
    headerHeight = (globalThis as any).innerWidth < 1920 ? 30 : 45,
    theme = 'dark',
    extraThemeProps = {},
    alternativeRowColor = '',
    gridRef = null,
    overlayLoadingTemplate = null,
    loadingOverlayComponent = '',
    loadingOverlayComponentParams = {},
    noRowsOverlayComponent = '',
    onPaginationChanged = () => {},
    onGridReady = () => {},
    onPageChanged = (page) => {},
    customPagination = false,
    getRowHeight = () => {},
    dataTestId,
    onSortChanged,
    rowModelType = 'clientSide',
}) => {
    const darkThemeParam = {
        backgroundColor: '#fff',

        headerFontSize: '0.583rem',
        headerFontWeight: '600',
        // cellStyle: { ...cellStyle, fontSize: '2.75rem' },
        headerTextColor: '#FFFF00',
        headerBackgroundColor: '#000000',
        ...extraThemeProps,
    }
    const lightThemeParam = {
        backgroundColor: '#fff',
        headerFontWeight: '600',
        // cellStyle: { ...cellStyle, fontSize: '2.75rem' },

        headerFontSize: '0.583rem',

        headerTextColor: '#4C4C50',
        headerBackgroundColor: '#F3F3F3',

        oddRowBackgroundColor: alternativeRowColor.length > 0 ? alternativeRowColor : '',
        ...extraThemeProps,
    }
    const myTheme = themeQuartz.withParams(theme == 'dark' ? darkThemeParam : lightThemeParam)

    const [currentPageSize, setCurrentPageSize] = useState(paginationPageSize)
    const [gridApi, setGridApi] = useState<any>(null)

    useEffect(() => {
        if (paginationPageSizeSelector && Array.isArray(paginationPageSizeSelector)) {
            if (!paginationPageSizeSelector.includes(currentPageSize)) {
                setCurrentPageSize(paginationPageSizeSelector[0])
            }
        }
    }, [currentPageSize, paginationPageSizeSelector])

    useEffect(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.setGridOption('paginationPageSize', currentPageSize)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPageSize])

    useEffect(() => {
        if (gridApi) {
            if (rowData == null) {
                gridRef.current?.api.showLoadingOverlay()
            } else if (rowData?.length == 0) {
                gridRef.current?.api.showNoRowsOverlay()
            } else {
                gridRef.current?.api.hideOverlay()
            }
        }
    }, [rowData, gridApi])

    return (
        <div>
            {/* AG Grid */}
            <div>
                <div data-testid={dataTestId} className={className} style={style}>
                    <AgGridReact
                        ref={gridRef}
                        gridId={gridId}
                        rowData={columnDefs ? rowData : null}
                        rowHeight={rowHeight}
                        rowStyle={{ width: '100%' }}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        theme={myTheme}
                        onGridReady={(params: any) => {
                            setGridApi(params.api)
                            onGridReady()
                        }}
                        gridOptions={gridOptions}
                        rowModelType={rowModelType}
                        paginationPageSizeSelector={paginationPageSizeSelector}
                        pagination={pagination}
                        paginateChildRows={true}
                        paginationPageSize={currentPageSize}
                        headerHeight={headerHeight}
                        overlayLoadingTemplate={overlayLoadingTemplate ?? ''}
                        loadingOverlayComponent={Loader}
                        noRowsOverlayComponent={rowData?.length === 0 ? noRowsOverlayComponent || NoRecordsFound : Loader}
                        onPaginationChanged={useCallback((data: any) => {
                            onPaginationChanged(data)
                        }, [])}
                        suppressMovableColumns={true}
                        getRowHeight={getRowHeight}
                        enableBrowserTooltips={true}
                        tooltipShowDelay={0}
                        tooltipHideDelay={2000}
                        onSortChanged={(params: any) => {
                            if (onSortChanged) {
                                const fcol = params.columns.find((col: any) => col.sort)
                                if (fcol) {
                                    onSortChanged({ col: fcol.colDef.headerName, sort: fcol.sort })
                                } else {
                                    onSortChanged(null)
                                }
                            }
                        }}
                        loading={rowData == null}
                    />
                </div>
                {customPagination && <TablePagination totalRecords={totalRecords} pageSize={paginationPageSize} currentPage={currentPage} onPageChange={(page: number) => onPageChanged(page)} />}
            </div>
        </div>
    )
}

export default CustomAgGrid
